import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import RelatedProducts from '../components/RelatedProducts';
import { products } from '../data/products';

const ProductDetail = () => {
  const { id } = useParams();
  const { addToCart } = useCart();
  const [product, setProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedColor, setSelectedColor] = useState('Black');
  const [selectedConnectivity, setSelectedConnectivity] = useState('Wireless');

  useEffect(() => {
    const foundProduct = products.find(p => p.id === parseInt(id));
    setProduct(foundProduct);
  }, [id]);

  const handleAddToCart = () => {
    if (product) {
      addToCart(product, quantity);
    }
  };

  const handleBuyNow = () => {
    if (product) {
      addToCart(product, quantity);
      // 这里可以导航到结账页面
      console.log('Buy Now clicked');
    }
  };

  const formatPrice = (price) => {
    // 如果价格已经是格式化的字符串（如 "S$22.90"），直接返回
    if (typeof price === 'string') {
      return price;
    }
    // 如果价格是数字，格式化为 "S$" 格式
    return `S$${price.toFixed(2)}`;
  };

  // 生成产品图片数组
  const generateImages = (product) => {
    if (!product) return [];
    
    const baseImages = [
      product.image,
      "https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=800&h=600&fit=crop",
      "https://images.unsplash.com/photo-1542751371-adc38448a05e?w=800&h=600&fit=crop",
      "https://images.unsplash.com/photo-1542751371-29b0f74f9713?w=800&h=600&fit=crop"
    ];
    
    return baseImages;
  };

  // 颜色选项
  const colorOptions = [
    { name: 'Black', description: 'Classic Black' },
    { name: 'White', description: 'Modern White' },
    { name: 'Blue', description: 'Electric Blue' }
  ];

  // 连接方式选项
  const connectivityOptions = ['Wireless', 'Wired'];

  // 当颜色改变时，更新主图
  useEffect(() => {
    if (product) {
      setSelectedImage(0); // 重置到第一张图片（颜色图片）
    }
  }, [selectedColor, product]);

  if (!product) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">❌</div>
          <h2 className="text-2xl font-bold text-white mb-2">Product Not Found</h2>
          <p className="text-gray-400 mb-6">Sorry, the product you're looking for doesn't exist.</p>
          <Link to="/shop">
            <button className="bg-green-600 hover:bg-green-700 text-white py-3 px-6 rounded-md font-medium transition-colors">
              Back to Store
            </button>
          </Link>
        </div>
      </div>
    );
  }

  const images = generateImages(product);

  return (
    <div className="min-h-screen bg-gray-900 pt-24 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <nav className="mb-8">
          <div className="flex items-center space-x-2 text-sm text-gray-400">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/shop" className="hover:text-white transition-colors">Shop</Link>
            <span>/</span>
            <Link to={`/shop/${product.category}`} className="hover:text-white transition-colors">
              {product.category}
            </Link>
            <span>/</span>
            <span className="text-white">{product.name}</span>
          </div>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
          {/* 左侧产品图片 */}
          <div>
            {/* 主图 */}
            <div className="bg-gray-800 rounded-lg overflow-hidden mb-4">
              <img
                src={images[selectedImage]}
                alt={product.name}
                className="w-full h-auto object-cover"
                onError={(e) => {
                  e.target.src = '/images/products/placeholder.jpg';
                }}
              />
            </div>
            
            {/* 缩略图 */}
            <div className="grid grid-cols-4 gap-2">
              {images.map((img, index) => (
                <button 
                  key={index}
                  onClick={() => setSelectedImage(index)}
                  className={`bg-gray-800 rounded-md overflow-hidden border-2 ${selectedImage === index ? 'border-green-500' : 'border-transparent'}`}
                >
                  <img 
                    src={img} 
                    alt={`${product.name} view ${index + 1}`} 
                    className="w-full h-auto"
                    onError={(e) => {
                      e.target.src = '/images/products/placeholder.jpg';
                    }}
                  />
                </button>
              ))}
            </div>
          </div>
          
          {/* 右侧产品信息 */}
          <div>
            {/* 商品标题 */}
            <h1 className="text-3xl font-bold mb-2 text-white">{product.name}</h1>
            
            {/* 评分 */}
            <div className="flex items-center mb-4">
              <div className="flex text-yellow-400">
                {[...Array(5)].map((_, i) => (
                  <span key={i} className={i < Math.floor(product.rating) ? "text-yellow-400" : "text-gray-600"}>★</span>
                ))}
              </div>
              <span className="ml-2 text-gray-400">({product.reviews} reviews)</span>
            </div>
            
            {/* 价格（含折扣显示） */}
            <div className="mb-6">
              <span className="text-3xl font-bold text-white">{formatPrice(product.price)}</span>
              {product.originalPrice && (
                <span className="ml-3 text-gray-400 line-through">{formatPrice(product.originalPrice)}</span>
              )}
              {product.originalPrice && (
                <span className="ml-3 bg-red-600 text-white px-2 py-1 rounded-md text-sm">
                  Save {formatPrice(product.originalPrice - product.price)}
                </span>
              )}
            </div>
            
            {/* 库存状态 */}
            <div className="mb-6">
              {product.inStock ? (
                <span className="text-green-500 flex items-center">
                  <svg className="w-5 h-5 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  In Stock
                </span>
              ) : (
                <span className="text-red-500 flex items-center">
                  <svg className="w-5 h-5 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  Out of Stock
                </span>
              )}
            </div>
            
            {/* 简短描述 */}
            <p className="text-gray-300 mb-6">{product.description}</p>
            
            {/* 关键特性 */}
            {product.features && product.features.length > 0 && (
              <div className="mb-6">
                <h3 className="text-lg font-medium mb-3 text-white">Key Features</h3>
                <div className="space-y-2">
                  {product.features.map((feature, index) => (
                    <div key={index} className="flex items-start">
                      <svg className="w-5 h-5 mr-2 text-green-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-gray-300">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* 颜色选项（带图片缩略图） */}
            <div className="mb-6">
              <h3 className="text-lg font-medium mb-3 text-white">Choose Color</h3>
              <div className="flex gap-3">
                {colorOptions.map((color) => (
                  <div key={color.name} className="flex flex-col">
                    <button
                      className={`w-[80px] h-[80px] border ${color.name === selectedColor ? 'border-2 border-green-500' : 'border border-gray-600'}
                        rounded-md bg-gray-800 flex items-center justify-center overflow-hidden relative hover:border-green-400 transition-colors`}
                      onClick={() => setSelectedColor(color.name)}
                    >
                      <div className="w-full h-full flex items-center justify-center">
                        <img
                          src={color.name === 'Black' 
                            ? 'https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=80&h=80&fit=crop&text=Black'
                            : color.name === 'White'
                            ? 'https://images.unsplash.com/photo-1542751371-adc38448a05e?w=80&h=80&fit=crop&text=White'
                            : 'https://images.unsplash.com/photo-1542751371-29b0f74f9713?w=80&h=80&fit=crop&text=Blue'
                          }
                          alt={color.name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.src = '/images/products/placeholder.jpg';
                          }}
                        />
                      </div>
                      {color.name === selectedColor && (
                        <div className="absolute top-0 right-0 w-0 h-0 border-t-[20px] border-r-[20px] border-t-transparent border-r-green-500"></div>
                      )}
                    </button>
                    <div className="mt-2 text-center">
                      <div className="text-sm text-white font-medium">{color.name}</div>
                      <div className="text-xs text-gray-400">{color.description}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Connectivity (Wireless / Wired) 按钮 */}
            <div className="mb-6">
              <h3 className="text-lg font-medium mb-3 text-white">Connectivity</h3>
              <div className="grid grid-cols-2 gap-3">
                {connectivityOptions.map((conn) => (
                  <button 
                    key={conn}
                    className={`border-2 ${conn === selectedConnectivity ? 'border-green-500' : 'border-gray-700'} 
                      rounded-md py-3 px-4 text-center hover:border-green-500 transition-colors text-white`}
                    onClick={() => setSelectedConnectivity(conn)}
                  >
                    {conn}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Quantity (+ / -) */}
            <div className="mb-8">
              <h3 className="text-lg font-medium mb-3 text-white">Quantity</h3>
              <div className="flex items-center">
                <button 
                  className="bg-gray-800 px-4 py-2 rounded-l-md hover:bg-gray-700 transition-colors text-white"
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                >
                  -
                </button>
                <input 
                  type="text" 
                  value={quantity} 
                  readOnly
                  className="bg-gray-800 text-center w-16 py-2 border-x border-gray-700 text-white"
                />
                <button 
                  className="bg-gray-800 px-4 py-2 rounded-r-md hover:bg-gray-700 transition-colors text-white"
                  onClick={() => setQuantity(quantity + 1)}
                >
                  +
                </button>
              </div>
            </div>
            
            {/* Add to Cart + Buy Now 按钮 */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <button 
                className="bg-green-600 hover:bg-green-700 text-white py-3 px-6 rounded-md font-medium transition-colors"
                onClick={handleAddToCart}
                disabled={!product.inStock}
              >
                Add to Cart
              </button>
              <button 
                className="bg-gray-800 hover:bg-gray-700 text-white py-3 px-6 rounded-md font-medium border border-gray-600 transition-colors"
                onClick={handleBuyNow}
                disabled={!product.inStock}
              >
                Buy Now
              </button>
            </div>
            
            {/* 底部小提示 */}
            <div className="border-t border-gray-700 pt-6">
              <div className="flex items-center mb-3">
                <svg className="w-5 h-5 mr-2 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
                  <path d="M3 4a1 1 0 00-1 1v10a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H10a1 1 0 001-1v-5h2a1 1 0 00.9-.5l1.5-2A1 1 0 0015 7h-3V4a1 1 0 00-1-1H3zM14 7l-1.5 2H10V5h4v2z" />
                </svg>
                <span className="text-gray-300">Available Shipping</span>
              </div>
              <div className="flex items-center">
                <svg className="w-5 h-5 mr-2 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                </svg>
                <span className="text-gray-300">Ships within 24 hours</span>
              </div>
            </div>
          </div>
        </div>

        {/* Related Products */}
        {product && (
          <RelatedProducts 
            currentProductId={product.id} 
            currentCategory={product.category} 
            limit={4} 
          />
        )}
      </div>
    </div>
  );
};

export default ProductDetail;
